import Timeline from "@/pages/taskpane/features/resources/components/Timeline";
import HStack from "@components/layout/HStack";
import VStack from "@components/layout/VStack";
import Button from "@components/ui/Button";
import Menu from "@components/ui/Menu";
import SlideUpPanel from "@components/ui/SlideUpPanel";
import Status from "@components/ui/Status";
import Tooltip from "@components/ui/Tooltip";
import { ResourceEntity } from "@features/resources/services/types/resourceEntity";
import { tooltipText } from "@shared/constants/appConstants";
import {
  useAppConfigStore,
  useAppointmentStore,
  useAttendeesStore,
  useOutlookStore,
  useResourceStore,
  useSettingStore,
  useThemeStore,
} from "@shared/stores";
import React, { useMemo, useState, useRef, useEffect, useLayoutEffect } from "react";
import { dateTimeToTicks, getOpeningTimeISOString } from "../ulti";
import ModalResourceDetail from "./ModalResourceDetail";
import FolderIcon from "@/assets/icons/Folder.svg";
import { QUERY_KEY } from "@/pages/taskpane/shared/constants/queryKey";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAddFavorite } from "../services/useAddFavorites";
import { useDeleteFavorite } from "../services/useDeleteFavorites";
import { formatDateForTimeline, getMeetingsForResourceAndDate, useGetTimeline } from "../services/useGetTimeline";
import AvailabilityBox from "./AvailabilityBox";
import IconPermission from "./IconPermission";
import ModalResourceCalendar from "./ModalResourceCalendar";
import {
  CalendarIcon,
  CheckmarkIcon,
  CityIcon,
  FavoriteIcon,
  InfoIcon,
  LocationIcon,
  MoreVertical,
  PeopleOutlineIcon,
  PlaceholderIcon,
  StarIcon,
  TrashIcon,
} from "@/pages/taskpane/components/icons/IconCustom";
import { EDisableRule, EStatusResource, ETypeOrderResource } from "../services/types/enum";
import { RULE_DESCRIPTION } from "@/pages/taskpane/shared/constants/permission";
import { useResourceService } from "../services/resourceService";
import { generateCId } from "@/pages/taskpane/lib/utils/randomUUID";
import {
  ICheckMoveOrderRequest,
  IOpenOrderRequest,
  //ISaveMovingOrderTempRequest,
  ISavePlaceOrderSessionDataRequest,
} from "@/pages/taskpane/lib/order/types/OrderEntity";
import { useOrderForm } from "../hooks/useOrderForm";
import { convertDateFormatToRc, convertTimeFormatToRc } from "@/pages/taskpane/lib/utils/dateTimeHelper";
import { useLocation } from "react-router-dom";
import { routes } from "@/pages/taskpane/shared/constants/routes";
import imageUrl from "@/assets/images/imageResource.svg";

type Props = {
  resource: ResourceEntity;
  isSelected?: boolean;
  onRemoveFromFavorite?: (resource: ResourceEntity) => void;
  isOrderedForm?: boolean;
};

const ResourceCard = ({ resource, isSelected, onRemoveFromFavorite, isOrderedForm }: Props) => {
  const { t } = useTranslation();
  const pathname = useLocation().pathname;
  const { organizer, recurringDates, timeZone, subject, iCalUId, customUid, globalId, guid } = useOutlookStore();
  // const showToast = useToastStore((state) => state.showToast);
  const { isDarkMode } = useThemeStore();
  const { tileConfiguration } = useSettingStore();
  const { startDateTimeZone, endDateTimeZone, startDate, endDate, startDateTimeUtc, endDateTimeUtc } =
    useAppointmentStore();
  const { toggleResource } = useResourceService();
  const [openDetail, setOpenDetail] = useState(false);
  const [openCalendar, setOpenCalendar] = useState(false);
  const {
    deskLocationList,
    selectedResource,
    originalSelectedResources,
    meetingLabel: meetingLabelSelected,
    setSelectedResource,
  } = useResourceStore();
  const [typeOrderResource, setTypeOrderResource] = useState<ETypeOrderResource>(ETypeOrderResource.NewOrder);
  const [timelineDisplayDate, setTimelineDisplayDate] = useState<string>(() => startDateTimeZone || "");
  const { mainAttendee, requiredAttendees, optionalAttendees } = useAttendeesStore();
  const { language, meetingLabel, parameters } = useAppConfigStore();
  const { dateFormat, timeFormat } = useSettingStore();
  const { openPlaceOrder } = useOrderForm();
  const categoriesWrapperRef = useRef<HTMLDivElement>(null);
  const [maxVisible, setMaxVisible] = useState<number | null>(null);
  const [isCalculated, setIsCalculated] = useState(false);

  const attendees = useMemo(() => {
    return requiredAttendees.concat(optionalAttendees);
  }, [requiredAttendees, optionalAttendees]);

  const queryClient = useQueryClient();

  React.useEffect(() => {
    checkTypeOrderResource();
  }, [originalSelectedResources, selectedResource]);

  // Update timeline display date when appointment date changes
  React.useEffect(() => {
    if (startDateTimeZone) {
      setTimelineDisplayDate(startDateTimeZone);
    }
  }, [startDateTimeZone]);

  // Calculate max visible categories
  useLayoutEffect(() => {
    if (!resource?.categories || resource.categories.length === 0) {
      setMaxVisible(null);
      setIsCalculated(true);
      return;
    }

    const wrapper = categoriesWrapperRef.current;
    if (!wrapper) {
      requestAnimationFrame(() => {
        const retryWrapper = categoriesWrapperRef.current;
        if (retryWrapper) {
          const width = retryWrapper.getBoundingClientRect().width;
          if (width > 0) {
            calculateMaxVisible(width);
          }
        }
      });
      return;
    }

    const width = wrapper.getBoundingClientRect().width;
    if (width > 0) {
      calculateMaxVisible(width);
    }

    function calculateMaxVisible(containerWidth: number): void {
      const iconWidth = 12;
      const gap = 4;
      const ellipsisWidth = 20;
      const totalCategories = resource.categories.length;
      const itemWidth = iconWidth + gap;

      // Try without ellipsis first
      const maxWithoutEllipsis = Math.floor(containerWidth / itemWidth);
      if (maxWithoutEllipsis >= totalCategories) {
        setMaxVisible(totalCategories);
        setIsCalculated(true);
        return;
      }

      // With ellipsis
      const availableWidth = containerWidth - ellipsisWidth - gap;
      const calculatedMax = Math.floor(availableWidth / itemWidth);
      setMaxVisible(Math.max(1, calculatedMax));
      setIsCalculated(true);
    }
  }, [resource?.categories]);

  const checkTypeOrderResource = () => {
    if (originalSelectedResources) {
      const resourceOrdered = originalSelectedResources.find((item) => item.id === resource.id);
      // if (selectedResource.length === 1 && originalSelectedResources.length === 1 && !resourceOrdered) {
      //   setTypeOrderResource(ETypeOrderResource.MoveOrder);
      //   return;
      // }
      if (resourceOrdered && resourceOrdered.isOrdered) {
        setTypeOrderResource(ETypeOrderResource.ChangeOrder);
        return;
      }
    }
  };

  // Fetch timeline data for this resource based on current display date
  const { data: timelineData } = useGetTimeline({
    payload: {
      resourceIds: [resource.id],
      referenceDate: timelineDisplayDate || "",
      organizerEmail: organizer || "",
      timeZoneStandardName: timeZone || "UTC",
    },
    enabled:
      parameters?.outlookAddinTimeLineVisible &&
      !!resource.id &&
      !!organizer &&
      !!timelineDisplayDate &&
      tileConfiguration?.timeline &&
      recurringDates.length === 0 && pathname !== routes.selectedResources,
  });

  // Convert timeline data to meetings format for Timeline component
  const timelineMeetings = useMemo(() => {
    if (!timelineData?.data || !timelineDisplayDate) return [];

    const dateKey = formatDateForTimeline(timelineDisplayDate);
    const meetings = getMeetingsForResourceAndDate(timelineData.data, resource.id, dateKey);

    // Convert to Timeline component format
    return meetings.map((meeting) => ({
      id: meeting.id.toString(),
      title: meeting.subject,
      startTime: meeting.from,
      endTime: meeting.to,
      preBufferTime: meeting.preBufferTime,
      postBufferTime: meeting.postBufferTime,
    }));
  }, [timelineData, resource.id, timelineDisplayDate]);

  // Handle timeline date change (when user navigates to different day)
  const handleTimelineDateChange = React.useCallback(
    (newDateString: string) => {
      // newDateString format: "YYYY-MM-DD" from Timeline component
      // Convert to ISO string with timezone similar to startDateTimeZone

      if (!startDateTimeZone) return;

      // Extract time and timezone from original startDateTimeZone
      // Supports formats:
      // - "2024-11-14T09:00:00+07:00"
      // - "2024-11-14T09:00:00.000+07:00"
      // - "2024-11-14T09:00:00Z"
      const timeMatch = startDateTimeZone.match(/T(.+)$/);
      const timePart = timeMatch ? timeMatch[1] : "00:00:00+00:00";

      // Combine new date with original time and timezone
      const newDateTimeZone = `${newDateString}T${timePart}`;

      setTimelineDisplayDate(newDateTimeZone);
      // console.log("📅 Timeline date changed:", newDateString, "→", newDateTimeZone);
    },
    [startDateTimeZone]
  );

  const isToggleResource = useMemo(() => {
    if (
      isSelected ||
      !resource?.availability?.isAvailable ||
      resource?.getDisableRule(selectedResource)?.length > 0 ||
      resource?.isDisableResourceByHotDeskRule(selectedResource, deskLocationList) ||
      resource?.availability?.isCannotBook(recurringDates.length > 0)
    )
      return false;
    return true;
  }, [isSelected, resource, recurringDates, selectedResource, deskLocationList]);

  const handleShowDetail = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    setOpenDetail(true);
  };

  const closeDetail = () => setOpenDetail(false);

  const handleUpdateFavoriteResource = (isAddToFavorite: boolean) => {
    if (selectedResource?.length && selectedResource.some((r) => r.id === resource.id)) {
      setSelectedResource(
        selectedResource.map((r) =>
          r.id === resource.id ? new ResourceEntity({ ...r, isFavoriteResource: isAddToFavorite }) : r
        )
      );
    }
    queryClient.setQueriesData({ queryKey: [QUERY_KEY.GET_RESOURCES] }, (oldData: any) => {
      if (!oldData) return oldData;
      if (oldData.pages) {
        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            resources: page.resources.map((r: ResourceEntity) => {
              if (r.id === resource.id) {
                return new ResourceEntity({ ...r, isFavoriteResource: isAddToFavorite });
              }
              return r;
            }),
          })),
        };
      }
      if (Array.isArray(oldData)) {
        return oldData.map((r: ResourceEntity) => {
          if (r.id === resource.id) {
            return new ResourceEntity({ ...r, isFavoriteResource: isAddToFavorite });
          }
          return r;
        });
      }
      return oldData;
    });
    if (onRemoveFromFavorite && !isAddToFavorite) {
      onRemoveFromFavorite(resource);
    }
    // showToast(t(isAddToFavorite ? "Resource added to favorite" : "Resource removed from favorite"), true);
  };

  const { mutate: addFavorite } = useAddFavorite({
    mutationConfig: {
      onSuccess: () => {
        handleUpdateFavoriteResource(true);
      },
      onError: () => {
        // showToast(t("Failed to add resource to favorite"), false);
      },
    },
  });

  const { mutate: deleteFavorite } = useDeleteFavorite({
    mutationConfig: {
      onSuccess: () => {
        handleUpdateFavoriteResource(false);
      },
    },
  });
  const handleAddToFavorite = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    if (resource.isFavoriteResource) {
      deleteFavorite({
        organizerEmail: organizer,
        resourceId: resource.id,
      });
    } else {
      addFavorite({
        organizerEmail: organizer,
        resourceId: resource.id,
      });
    }
  };

  const handleShowCalendar = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenCalendar(true);
  };

  const handleCloseCalendar = () => {
    setOpenCalendar(false);
  };

  const handleNewOrder = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    const sessionId = generateCId();

    const requestSaveSession: ISavePlaceOrderSessionDataRequest = {
      SessionId: sessionId,
      Attendees: attendees.map((attendee) => ({
        Email: attendee?.email || "",
        Name: attendee?.name || "",
        IsRequired: attendee?.isRequired || false,
      })),
      RecurringInstances:
        recurringDates && recurringDates.length > 0
          ? recurringDates.map((date) => dateTimeToTicks(new Date(date.startDate))).join(";")
          : "",
      ConflictResourcesOccurrencesMap: "",
      SelectedResourceIds: (selectedResource || [])?.map((r) => r.id),
      ExternalResourceIds: (selectedResource || []).filter((r) => r.isExternal).map((r) => r.id),
    };
    const requestOpenOrder: IOpenOrderRequest = {
      SelectedResources: resource.id.toString(),
      StartTime: dateTimeToTicks(new Date(startDateTimeUtc)).toString() || "",
      EndTime: dateTimeToTicks(new Date(endDateTimeUtc)).toString() || "",
      // OriginalEndTime: dateTimeToTicks(new Date(endDateTimeUtc)).toString() || "",
      // OriginalStartTime: dateTimeToTicks(new Date(startDateTimeUtc)).toString() || "",
      Subject: subject || "",
      OrganizerEmail: mainAttendee?.email || "",
      OrganizerName: mainAttendee?.name || "",
      TimeZoneCode: timeZone || "",
      CustomUid: customUid || "",
      Language: language || "en",
      GlobalId: globalId || "",
      Guid: guid || "",
      SessionId: sessionId,
      ReOpenOrderFormAfterSend: globalId ? "1" : "0",
      clientTimeFormat: convertTimeFormatToRc(timeFormat),
      clientDateFormat: convertDateFormatToRc(dateFormat),
      IsRecurringApp: recurringDates && recurringDates.length > 0 ? "1" : "0",
    };

    let requestMoveOrder: ICheckMoveOrderRequest | undefined;
    if (typeOrderResource === ETypeOrderResource.MoveOrder) {
      requestMoveOrder = {
        OldGuid: customUid || "",
        NewResourceId: resource.id,
        StartTime: dateTimeToTicks(new Date(startDateTimeUtc)),
        EndTime: dateTimeToTicks(new Date(endDateTimeUtc)),
        TimeZoneName: timeZone || "",
        OrganizerEmail: organizer || "",
      };
    }
    openPlaceOrder(resource, typeOrderResource, requestSaveSession, requestOpenOrder, requestMoveOrder, {
      meetingLabelSelected: meetingLabelSelected ?? 0,
      isNewMeeting: !iCalUId,
      meetingLabelOptions: meetingLabel?.meetingLabels ?? [],
      startDate: startDateTimeUtc || "",
      endDate: endDateTimeUtc || "",
    });
  };

  const handleCardClick = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!isToggleResource) return;
    await toggleResource(resource);
  };

  const handleRemoveClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    await toggleResource(resource);
  };

  const capacity = useMemo(() => {
    if (recurringDates?.length > 0) {
      return resource.availability.allowCapacity;
    } else {
      if (resource.isPooledResource) {
        return `${resource.availability.remainingCapacity}/${resource.availability.allowCapacity}`;
      } else {
        return resource.capacity;
      }
    }
  }, [resource]);

  const menuOptions = useMemo(() => {
    return [
      {
        id: 1,
        name: t("Calendar"),
        icon: <Button leftIcon={<CalendarIcon isDarkMode={isDarkMode} />} />,
        action: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
          handleShowCalendar(e);
        },
      },
      {
        id: 2,
        name: t("View Detail"),
        icon: <Button leftIcon={<InfoIcon isDarkMode={isDarkMode} />} />,
        action: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
          handleShowDetail(e);
        },
      },
      {
        id: 3,
        name: resource?.isFavoriteResource ? t("Remove from favorites") : t("Add to favorites"),
        icon: (
          <Button
            leftIcon={
              resource?.isFavoriteResource ? (
                <FavoriteIcon isDarkMode={isDarkMode} />
              ) : (
                <StarIcon isDarkMode={isDarkMode} isOffHover={true} />
              )
            }
          />
        ),
        action: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
          handleAddToFavorite(e);
        },
      },
    ];
  }, [resource, t]);

  const tooltipStatus = useMemo(() => {
    if (!resource?.availability?.isAvailable) {
      return [];
    }
    if (resource?.getDisableRule) {
      let tooltip = [];
      const disableRule = resource?.getDisableRule(selectedResource);
      if (disableRule.includes(EDisableRule.DisablePooledResource)) {
        tooltip.push(t(RULE_DESCRIPTION[EDisableRule.DisablePooledResource]));
      }
      if (disableRule.includes(EDisableRule.DisableVRResource)) {
        tooltip.push(t(RULE_DESCRIPTION[EDisableRule.DisableVRResource]));
      }
      if (disableRule.includes(EDisableRule.DisableOpeningHours)) {
        tooltip.push(t(RULE_DESCRIPTION[EDisableRule.DisableOpeningHours]));
      }
      if (resource?.isDisableResourceByHotDeskRule(selectedResource, deskLocationList)) {
        tooltip.push(t(RULE_DESCRIPTION[EDisableRule.DisableHotDeskResource]));
      }
      return tooltip;
    } else {
      return [];
    }
  }, [resource, selectedResource, t, deskLocationList]);

  const renderMessageBtnOrder = useMemo(() => {
    if (resource?.isPooledResource || resource?.isLightResource) {
      return t("It is not possible to order services on the selected resource.");
    }
    if (iCalUId && recurringDates.length > 0) {
      return t(
        "As this is a Recurring meeting, please use 'My Meetings', the individual Outlook meeting of the series or the Recurring reservation or order email to handle the orders for this series."
      );
    }
    return "";
  }, [resource, iCalUId, recurringDates]);

  const statusResource = useMemo(() => {
    if (!resource?.availability?.isAvailable) {
      return EStatusResource.Unavailable;
    }
    if (
      (resource?.getDisableRule(selectedResource)?.length > 0 ||
        resource?.isDisableResourceByHotDeskRule(selectedResource, deskLocationList)) &&
      !isSelected
    ) {
      return EStatusResource.Disable;
    }
    return EStatusResource.Available;
  }, [resource, selectedResource, deskLocationList]);

  return (
    <VStack
      className={`border border-lightGray rounded-lg p-3 bg-white hover:bg-extraLightGray dark:hover:bg-deepGray dark:bg-darkBackgroundColor dark:border-darkMediumGray ${isToggleResource ? "cursor-pointer" : "cursor-default"
        }`}
      gap={1}
      onClick={handleCardClick}
      data-resource-id={resource?.id}
    >
      <HStack justifyContent="between" alignItems="center" className="w-full">
        <HStack gap={1} className="flex-1 min-w-0">
          <div className="flex-shrink-0">
            <Status status={statusResource} tooltipText={tooltipStatus.join("\n")} />
          </div>
          <p className="font-semibold text-sm truncate min-w-0 flex-1" title={resource?.name}>
            {resource?.name}
          </p>
        </HStack>

        <HStack alignItems="center" gap={2}>
          {/* Icon Permission */}
          <IconPermission resource={resource} />

          {/* Remove */}
          {isSelected ? (
            <Tooltip message={t(tooltipText.remove)} position="top">
              <Button leftIcon={<TrashIcon isDarkMode={isDarkMode} />} onClick={handleRemoveClick} />
            </Tooltip>
          ) : null}

          <Menu width="200px" options={menuOptions} icon={<MoreVertical isDarkMode={isDarkMode} size={20} />} />
        </HStack>
      </HStack>

      <VStack gap={2} width="100%">
        {/* Resource Info */}
        <HStack className="w-full gap-[6px]">
          {tileConfiguration?.resourceType && resource?.resourceType ? (
            <>
              <HStack gap={1} className="max-w-[40%]">
                <CityIcon isDarkMode={isDarkMode} size={16} />
                <p className="text-xs font-normal truncate">{resource?.resourceType}</p>
              </HStack>
              {tileConfiguration?.resourceType && (tileConfiguration?.capacity || tileConfiguration?.location) ? (
                <PlaceholderIcon isDarkMode={isDarkMode} size={4} />
              ) : null}
            </>
          ) : null}

          {tileConfiguration?.location ? (
            <HStack gap={1} className={`${isSelected ? "max-w-[80%]" : "max-w-[40%]"}`}>
              <LocationIcon isDarkMode={isDarkMode} size={16} />
              <p className="text-xs font-normal truncate">{resource?.location}</p>
            </HStack>
          ) : null}

          {tileConfiguration?.location && tileConfiguration?.capacity ? (
            <PlaceholderIcon isDarkMode={isDarkMode} size={4} />
          ) : null}

          {tileConfiguration?.capacity ? (
            <HStack gap={1}>
              <PeopleOutlineIcon isDarkMode={isDarkMode} size={16} />
              <p className="text-xs font-normal">{capacity}</p>
            </HStack>
          ) : null}
        </HStack>

        {/* Categories */}
        <HStack justifyContent="between" alignItems="start" className="w-full">
          <div
            ref={categoriesWrapperRef}
            className={`overflow-hidden ${isSelected ? "max-w-[70%]" : "flex-1"}`}
            style={{ width: isSelected ? undefined : '100%' }}
          >
            <HStack
              gap={1}
              className="flex-nowrap overflow-hidden"
              style={{ visibility: isCalculated ? 'visible' : 'hidden' }}
            >
              {tileConfiguration?.category &&
                resource?.categories?.length > 0 &&
                isCalculated &&
                maxVisible !== null &&
                (() => {
                  const visibleCategories = resource.categories.slice(0, maxVisible);
                  const hasMore = resource.categories.length > maxVisible;

                  return (
                    <>
                      {visibleCategories.map((category) => (
                        <Tooltip message={category.caption} position="top" key={category.id}>
                          <img
                            src={category.iconUrl ? category.iconUrl : FolderIcon}
                            alt={category.caption}
                            className="w-3 h-3 flex-shrink-0"
                            style={{ width: '12px', height: '12px', minWidth: '12px', flexShrink: 0 }}
                          />
                        </Tooltip>
                      ))}
                      {hasMore && (
                        <Tooltip
                          message={resource.categories.slice(maxVisible).map((cat) => cat.caption).join(", ")}
                          maxLines={5}
                          position="top"
                        >
                          <span className="text-xs text-mediumGray dark:text-midGray flex-shrink-0 whitespace-nowrap">
                            ...
                          </span>
                        </Tooltip>
                      )}
                    </>
                  );
                })()}
              {/* Placeholder to maintain layout during calculation */}
              {!isCalculated && resource?.categories && resource.categories.length > 0 && (
                <>
                  {resource.categories.map((category) => (
                    <div
                      key={`placeholder-${category.id}`}
                      className="w-3 h-3 flex-shrink-0"
                      style={{ width: '12px', height: '12px', minWidth: '12px', flexShrink: 0 }}
                      aria-hidden="true"
                    />
                  ))}
                </>
              )}
            </HStack>
          </div>

          {isSelected && parameters?.orderForm ? (
            resource?.isPooledResource || resource?.isLightResource || (iCalUId && recurringDates.length > 0) ? (
              <Tooltip message={renderMessageBtnOrder} position="top">
                <Button
                  isDefault={true}
                  className="px-2 py-1 text-xs font-normal text-end"
                  label={t(typeOrderResource)}
                  disabled={true}
                />
              </Tooltip>
            ) : (
              <Button
                onClick={handleNewOrder}
                isDefault={true}
                className="px-2 py-1 text-xs font-normal text-end flex-shrink-0"
                label={t(typeOrderResource)}
                leftIcon={isOrderedForm && typeOrderResource === ETypeOrderResource.NewOrder ? <CheckmarkIcon isDarkMode={isDarkMode} /> : null}
                gap={isOrderedForm ? 4 : 8}
              />
            )
          ) : null}
        </HStack>
      </VStack>

      {/* Timeline */}
      {!isSelected && parameters?.outlookAddinTimeLineVisible && tileConfiguration?.timeline && recurringDates.length === 0
        ? (() => {
          // Get resource opening hours as ISO strings (handles both string ISO and number formats)
          // Ensure displayDate is always a string (handle null, undefined, or other types)
          const displayDate = typeof startDateTimeZone === "string" ? startDateTimeZone : "";
          const resourceOpenStartTime = resource?.openStartTime
            ? getOpeningTimeISOString(resource?.openStartTime, displayDate)
            : "";
          const resourceOpenEndTime = resource?.openEndTime
            ? getOpeningTimeISOString(resource?.openEndTime, displayDate)
            : "";

          // Get buffer times from resource (in minutes)
          const resourcePreBufferTime = resource?.preBufferTime ?? 0;
          const resourcePostBufferTime = resource?.postBufferTime ?? 0;
          // const resourcePreBufferTime = 30;
          // const resourcePostBufferTime = 30;

          const openingDate = resource?.openingDate;
          // console.log("🚀 ~ ResourceCard ~ openingDate:", openingDate);

          const closingDate = resource?.closingDate;
          // console.log("🚀 ~ ResourceCard ~ closingDate:", closingDate);

          return (
            <Timeline
              isResourceAvailable={resource?.availability?.isAvailable}
              selectedStartTime={startDateTimeZone || (startDate ? startDate.toISOString() : "")}
              selectedEndTime={endDateTimeZone || (endDate ? endDate.toISOString() : "")}
              meetings={timelineMeetings}
              openStartTime={resourceOpenStartTime}
              openEndTime={resourceOpenEndTime}
              openingDate={openingDate}
              closingDate={closingDate}
              closeDates={[]}
              preBufferTime={resourcePreBufferTime}
              postBufferTime={resourcePostBufferTime}
              currentDate={timelineDisplayDate}
              onDateChange={handleTimelineDateChange}
            />
          );
        })()
        : null}

      {/* AvailabilityBox */}
      {!isSelected && recurringDates?.length > 0 ? (
        <HStack justifyContent="end" className="w-full">
          <AvailabilityBox resource={resource} />
        </HStack>
      ) : null}

      {/* Description */}
      {isSelected ? (
        <VStack gap={3} className="mt-2">
          <img src={resource.imageUrl || imageUrl} alt="resource" className="w-full h-[150px] object-cover rounded-[4px]" />
          {resource?.description ? <p className="text-xs">{resource.description}</p> : null}
        </VStack>
      ) : null}

      {/* Slide-up modal */}
      <SlideUpPanel
        isOpen={openDetail}
        onClose={closeDetail}
        title={resource?.name}
        children={<ModalResourceDetail resource={resource} />}
        iconRight={resource?.isFavoriteResource ? <FavoriteIcon /> : null}
      />
      {/* Modal Resource Calendar */}
      <SlideUpPanel
        isOpen={openCalendar}
        onClose={handleCloseCalendar}
        title={resource?.name}
        children={<ModalResourceCalendar resource={resource} />}
      />
    </VStack>
  );
};

export default ResourceCard;
